/**
 * The chart, the engine role map, and the cash-flow classification.
 *
 * The cash-flow column is here rather than on the cash-flow statement because
 * which bucket an account belongs in is an accounting judgement about that
 * account, not about a report. Two reports deciding it separately is how they
 * come to disagree about the same account.
 *
 * An untagged account that moved is what breaks the cash-flow statement, and
 * the statement names it and points here. A report that names a fix and gives
 * nobody a way to make it is a report people learn to scroll past.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Account, type RoleMapping } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Loading, Note, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { Tabs, matches } from '../components/SidePanel'

const TYPE_ORDER = ['asset', 'liability', 'equity', 'income', 'expense']

const CASH_FLOW_CLASSES: { value: string; label: string }[] = [
  { value: '', label: 'not classified' },
  { value: 'operating', label: 'operating' },
  { value: 'investing', label: 'investing' },
  { value: 'financing', label: 'financing' },
  { value: 'noncash_addback', label: 'non-cash, added back' },
  { value: 'cash', label: 'cash — the thing being explained' },
]

type Tab = 'accounts' | 'roles' | 'unmapped'

export default function ChartOfAccounts() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const configurable = can('org.coa.configure')
  const [saving, setSaving] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('accounts')
  const [search, setSearch] = useState('')
  const [type, setType] = useState('')
  const [onlyUnclassified, setOnlyUnclassified] = useState(false)

  const classify = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      api.patch<Account>(`/coa/accounts/${id}`, {
        cash_flow_class: value || null,
        clear_cash_flow_class: value === '',
      }),
    onSettled: () => {
      setSaving(null)
      void queryClient.invalidateQueries({ queryKey: ['coa'] })
      void queryClient.invalidateQueries({ queryKey: ['cash-flow'] })
    },
  })

  const accounts = useQuery({
    queryKey: ['coa'],
    queryFn: () => api.get<Account[]>('/coa/accounts'),
  })
  const roleMap = useQuery({
    queryKey: ['coa', 'role-map'],
    queryFn: () => api.get<RoleMapping[]>('/coa/role-map'),
  })
  const allRoles = useQuery({
    queryKey: ['coa', 'roles'],
    queryFn: () => api.get<string[]>('/coa/roles'),
  })

  const topUp = useMutation({
    mutationFn: () =>
      api.post<{ added: string[]; mapped: string[]; left_alone: string[] }>(
        '/coa/standard-chart/add-missing',
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coa'] })
    },
  })

  const rows = accounts.data ?? []
  const roles = roleMap.data ?? []
  const mapped = new Set(roles.map((m) => m.system_role))
  const unmapped = (allRoles.data ?? []).filter((r) => !mapped.has(r))
  const ordered = [...rows].sort(
    (a, b) =>
      TYPE_ORDER.indexOf(a.account_type) - TYPE_ORDER.indexOf(b.account_type) ||
      a.path.localeCompare(b.path),
  )
  const shownAccounts = ordered.filter(
    (a) =>
      (!type || a.account_type === type) &&
      (!onlyUnclassified || (a.is_postable && !a.cash_flow_class)) &&
      matches(search, a.code, a.name),
  )
  const shownRoles = roles.filter((m) =>
    matches(search, m.system_role, m.account_code, m.account_name),
  )
  const shownUnmapped = unmapped.filter((r) => matches(search, r))
  const pagedAccounts = usePaged(shownAccounts, `${type}|${search}|${onlyUnclassified}`, 50)
  const pagedRoles = usePaged(shownRoles, search, 50)

  if (accounts.isLoading) return <Loading what="Loading the chart" />
  const postable = rows.filter((a) => a.is_postable)
  const unclassified = postable.filter((a) => !a.cash_flow_class)
  const filtered = !!(search || type || onlyUnclassified)

  return (
    <>
      <ErrorNote error={accounts.error ?? classify.error ?? topUp.error} />

      {configurable && (
        <div className="bank-toolbar">
          <button disabled={topUp.isPending} onClick={() => topUp.mutate()}>
            {topUp.isPending ? 'Adding…' : 'Add missing standard accounts and roles'}
          </button>
          <span className="muted" style={{ fontSize: 13 }}>
            adds what this chart lacks; changes nothing that exists
          </span>
        </div>
      )}
      {topUp.data && (
        <div className="bank-result" style={{ padding: '0 0 12px' }}>
          <Note kind="ok">
            {topUp.data.added.length === 0 && topUp.data.mapped.length === 0
              ? 'Nothing was missing.'
              : `Added ${topUp.data.added.length} account(s): ${topUp.data.added.join(', ') || '—'}. Mapped ${topUp.data.mapped.length} role(s): ${topUp.data.mapped.join(', ') || '—'}.`}
            {topUp.data.left_alone.length > 0 &&
              ` Left alone: ${topUp.data.left_alone.join('; ')}.`}
          </Note>
          <button className="quiet small" onClick={() => topUp.reset()}>
            Dismiss
          </button>
        </div>
      )}

      {rows.length === 0 && (
        <Note kind="warn">
          The chart is empty. Nothing can be posted until it has accounts and the engine roles are
          mapped to them.
        </Note>
      )}

      <div className="grid">
        <Stat
          label="Accounts"
          value={String(rows.length)}
          note={`${postable.length} postable · ${rows.length - postable.length} groups`}
        />
        <Stat
          label="Not classified for cash flow"
          value={String(unclassified.length)}
          note={
            unclassified.length
              ? 'postable accounts the cash-flow statement cannot place'
              : 'every postable account has its bucket'
          }
        />
        <Stat
          label="Engine roles mapped"
          value={`${roles.length} of ${allRoles.data?.length ?? roles.length}`}
          note="what a posting template resolves when it asks for a role"
        />
        <Stat
          label="Roles without an account"
          value={String(unmapped.length)}
          note="not an error until a template uses one"
        />
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              { key: 'accounts', label: 'Accounts', count: rows.length },
              { key: 'roles', label: 'Engine role map', count: roles.length },
              {
                key: 'unmapped',
                label: 'Unmapped roles',
                count: unmapped.length,
                quietWhenZero: true,
              },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder={tab === 'accounts' ? 'Code or name' : 'Role or account'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search"
            />
            {tab === 'accounts' && (
              <>
                <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Type">
                  <option value="">Every type</option>
                  {TYPE_ORDER.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <label className="appr-check">
                  <input
                    type="checkbox"
                    checked={onlyUnclassified}
                    onChange={(e) => setOnlyUnclassified(e.target.checked)}
                  />
                  only not classified
                </label>
              </>
            )}
          </div>
        </header>

        <div className="body tight">
          {tab === 'accounts' &&
            (shownAccounts.length === 0 ? (
              <div className="empty">
                {rows.length === 0 ? 'No accounts yet.' : 'No account matches that.'}
              </div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Postable</th>
                    <th>Cash flow</th>
                    <th>Requires</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedAccounts.rows.map((a) => {
                    const requires = [
                      a.requires_location && 'location',
                      a.requires_department && 'department',
                      a.requires_party && 'party',
                    ].filter(Boolean)
                    return (
                      <tr key={a.id} className={a.is_postable ? undefined : 'coa-group'}>
                        <td
                          className={`mono ${filtered ? '' : `indent-${Math.min(a.depth - 1, 3)}`}`}
                        >
                          {a.code}
                        </td>
                        <td style={{ fontWeight: a.is_postable ? 400 : 600 }}>{a.name}</td>
                        <td className="muted">{a.account_type}</td>
                        <td>
                          {a.is_postable ? (
                            <span className="pill approved">postable</span>
                          ) : (
                            <span className="faint">group</span>
                          )}
                        </td>
                        <td>
                          {!a.is_postable ? (
                            <span className="faint">—</span>
                          ) : configurable ? (
                            <select
                              className={a.cash_flow_class ? undefined : 'unset'}
                              value={a.cash_flow_class ?? ''}
                              disabled={saving === a.id}
                              aria-label={`Cash flow class for ${a.code}`}
                              onChange={(e) => {
                                setSaving(a.id)
                                classify.mutate({ id: a.id, value: e.target.value })
                              }}
                            >
                              {CASH_FLOW_CLASSES.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </select>
                          ) : a.cash_flow_class ? (
                            <span className="muted">{a.cash_flow_class}</span>
                          ) : (
                            <span className="pill advisory">not classified</span>
                          )}
                        </td>
                        <td className="muted">
                          {requires.length ? requires.join(', ') : <span className="faint">—</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ))}

          {tab === 'roles' &&
            (roles.length === 0 ? (
              <div className="empty">
                No roles mapped yet. A template that asks for an unmapped role fails loudly rather
                than quietly posting to suspense.
              </div>
            ) : shownRoles.length === 0 ? (
              <div className="empty">No role matches that.</div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Account</th>
                    <th>Effective from</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRoles.rows.map((m) => (
                    <tr key={m.system_role}>
                      <td className="mono">{m.system_role}</td>
                      <td>
                        <span className="mono">{m.account_code}</span> {m.account_name}
                      </td>
                      <td className="muted date">{m.effective_from}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}

          {tab === 'unmapped' && (
            <div style={{ padding: 16 }}>
              <p className="muted" style={{ marginTop: 0 }}>
                Not an error. A role only needs an account when a template actually uses it — and
                when one does without a mapping, posting is refused with a message naming the role,
                rather than landing the amount somewhere plausible.
              </p>
              {shownUnmapped.length === 0 ? (
                <div className="empty">
                  {unmapped.length === 0 ? 'Every role has an account.' : 'No role matches that.'}
                </div>
              ) : (
                <div className="chip-list">
                  {shownUnmapped.map((r) => (
                    <span key={r} className="chip mono">
                      {r}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {tab === 'accounts' && (
          <Pager
            page={pagedAccounts.page}
            pages={pagedAccounts.pages}
            onPage={pagedAccounts.setPage}
          />
        )}
        {tab === 'roles' && (
          <Pager page={pagedRoles.page} pages={pagedRoles.pages} onPage={pagedRoles.setPage} />
        )}
      </section>
    </>
  )
}
